import { computed, inject, Injectable, NgZone, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';
import { pcmToBase64, PCM_WORKLET_SOURCE } from '../ai-chatbot/voice-audio';

export interface TranscriptSegment {
  text: string;
  timestamp: number;
}
export type ScribeStatus =
  | 'idle'
  | 'connecting'
  | 'recording'
  | 'stopping'
  | 'error';

interface VoiceSession {
  owner: object;
  disposed: boolean;
  ws?: WebSocket;
  stream?: MediaStream;
  context?: AudioContext;
  worklet?: AudioWorkletNode;
  source?: MediaStreamAudioSourceNode;
  recordingTimer?: ReturnType<typeof setTimeout>;
  handshakeTimer?: ReturnType<typeof setTimeout>;
  partialTimer?: ReturnType<typeof setTimeout>;
  finalTimer?: ReturnType<typeof setTimeout>;
  settleTimer?: ReturnType<typeof setTimeout>;
  pendingPartial?: string;
  pendingTimed: string[];
  rejectConnection?: (error: Error) => void;
  stopPromise?: Promise<string>;
  resolveStop?: (text: string) => void;
  onFlushed?: () => void;
  commitSent: boolean;
  audioSent: boolean;
}

@Injectable({ providedIn: 'root' })
export class ScribeService {
  private readonly http = inject(HttpClient);
  private readonly zone = inject(NgZone);
  private readonly defaultOwner = {};
  private session?: VoiceSession;
  readonly owner = signal<object | null>(null);
  readonly status = signal<ScribeStatus>('idle');
  readonly partialText = signal('');
  readonly committedSegments = signal<TranscriptSegment[]>([]);
  readonly errorMessage = signal('');
  readonly stoppedByTimeout = signal(false);
  readonly finalizationIncomplete = signal(false);
  readonly fullTranscript = computed(() =>
    [
      ...this.committedSegments().map((segment) => segment.text),
      this.partialText(),
    ]
      .filter(Boolean)
      .join(' ')
      .trim()
  );
  readonly isRecording = computed(() => this.status() === 'recording');
  readonly isBusy = computed(() =>
    ['connecting', 'recording', 'stopping'].includes(this.status())
  );
  readonly MODEL_ID = 'scribe_v2_realtime';
  readonly SAMPLE_RATE = 16000;
  readonly MAX_RECORDING_MS = 60000;
  readonly FINALIZE_TIMEOUT_MS = 2000;
  readonly MAX_SOCKET_BUFFERED_BYTES = 64000;

  isOwnedBy(owner: object): boolean {
    return this.owner() === owner;
  }
  private current(session: VoiceSession): boolean {
    return this.session === session && !session.disposed;
  }

  async startTranscription(owner: object = this.defaultOwner): Promise<void> {
    if (this.isBusy()) return;
    this.reset();
    const session: VoiceSession = {
      owner,
      disposed: false,
      pendingTimed: [],
      commitSent: false,
      audioSent: false,
    };
    this.session = session;
    this.owner.set(owner);
    this.status.set('connecting');
    try {
      if (
        !navigator.mediaDevices?.getUserMedia ||
        typeof AudioContext === 'undefined' ||
        typeof AudioWorkletNode === 'undefined'
      ) {
        throw new Error('Voice input is not supported in this browser.');
      }
      // Both operations start only after the microphone button is pressed.
      // No audio is sent until permission, capture, and the provider are ready.
      await Promise.all([
        this.prepareAudio(session),
        this.getToken().then((token) => {
          if (!this.current(session)) throw new Error('Voice input cancelled');
          return this.connectWebSocket(session, token);
        }),
      ]);
      if (!this.current(session)) return;
      this.zone.runOutsideAngular(() => {
        session.source!.connect(session.worklet!);
        // The processor has a silent output, keeping the audio graph active
        // without playing microphone sound through the user's speakers.
        session.worklet!.connect(session.context!.destination);
        session.recordingTimer = setTimeout(
          () => this.recordingLimit(session),
          this.MAX_RECORDING_MS
        );
      });
      this.status.set('recording');
    } catch (error) {
      if (!this.current(session)) return;
      const name = error instanceof Error ? error.name : '';
      this.fail(
        session,
        name === 'NotAllowedError'
          ? 'Microphone access was not allowed.'
          : name === 'NotFoundError'
          ? 'No microphone was found.'
          : 'Voice input could not start. Please try again.'
      );
    }
  }

  stopTranscription(owner: object = this.defaultOwner): Promise<string> {
    if (!this.isOwnedBy(owner)) return Promise.resolve('');
    const session = this.session;
    if (!session) return Promise.resolve(this.fullTranscript());
    if (session.stopPromise) return session.stopPromise;
    session.stopPromise = new Promise((resolve) => {
      session.resolveStop = resolve;
    });
    const promise = session.stopPromise;
    if (this.status() === 'connecting') {
      this.completeStop(session, true);
      return promise;
    }
    this.status.set('stopping');
    clearTimeout(session.recordingTimer);
    // Stop physical capture now; keep only the worklet/socket long enough to
    // flush the tail packet and receive the committed final words.
    session.source?.disconnect();
    session.stream?.getTracks().forEach((track) => track.stop());
    this.zone.runOutsideAngular(() => {
      session.finalTimer = setTimeout(
        () => this.completeStop(session, false),
        this.FINALIZE_TIMEOUT_MS
      );
      const commit = () => {
        if (!this.current(session) || session.commitSent) return;
        if (!session.audioSent || session.ws?.readyState !== WebSocket.OPEN) {
          this.completeStop(session, !session.audioSent);
          return;
        }
        session.commitSent = true;
        try {
          session.ws.send(
            JSON.stringify({
              message_type: 'input_audio_chunk',
              audio_base_64: '',
              commit: true,
            })
          );
        } catch {
          this.fail(
            session,
            'Voice input disconnected. Your captured text has been kept.'
          );
        }
      };
      session.onFlushed = commit;
      if (session.worklet) session.worklet.port.postMessage({ type: 'flush' });
      else commit();
    });
    return promise;
  }

  reset(owner?: object): void {
    if (owner && !this.isOwnedBy(owner)) return;
    if (this.session) {
      const session = this.session,
        resolve = session.resolveStop,
        text = this.fullTranscript();
      this.dispose(session);
      resolve?.(text);
    }
    this.session = undefined;
    this.owner.set(null);
    this.status.set('idle');
    this.partialText.set('');
    this.committedSegments.set([]);
    this.errorMessage.set('');
    this.stoppedByTimeout.set(false);
    this.finalizationIncomplete.set(false);
  }

  private async getToken(): Promise<string> {
    const result = await firstValueFrom(
      this.http
        .post<{ token: string }>(`${environment.cfBackend}/scribe/token`, {})
        .pipe(timeout(10000))
    );
    return result.token;
  }

  private connectWebSocket(
    session: VoiceSession,
    token: string
  ): Promise<void> {
    return this.zone.runOutsideAngular(
      () =>
        new Promise((resolve, reject) => {
          const params = new URLSearchParams({
            model_id: this.MODEL_ID,
            token,
            commit_strategy: 'vad',
            vad_silence_threshold_secs: '1.5',
            include_timestamps: 'true',
            include_language_detection: 'true',
            audio_format: 'pcm_16000',
          });
          const ws = (session.ws = new WebSocket(
            `wss://api.elevenlabs.io/v1/speech-to-text/realtime?${params}`
          ));
          session.rejectConnection = reject;
          session.handshakeTimer = setTimeout(
            () => reject(new Error('Voice connection timeout')),
            10000
          );
          // The handshake is complete at session_started, not merely on socket open.
          ws.onmessage = (event) => {
            if (!this.current(session)) return;
            let message: any;
            try {
              message = JSON.parse(event.data);
            } catch {
              return;
            }
            if (!message || typeof message !== 'object') return;
            if (message.message_type === 'session_started') {
              clearTimeout(session.handshakeTimer);
              session.rejectConnection = undefined;
              resolve();
            } else this.handleMessage(session, message);
          };
          ws.onerror = () =>
            this.fail(
              session,
              'Voice input disconnected. Your captured text has been kept.'
            );
          ws.onclose = () => {
            if (!this.current(session)) return;
            if (this.status() === 'stopping') this.completeStop(session, false);
            else
              this.fail(
                session,
                'Voice input disconnected. Your captured text has been kept.'
              );
          };
        })
    );
  }

  private handleMessage(session: VoiceSession, message: any): void {
    const type = message.message_type;
    if (type === 'partial_transcript') {
      const text = typeof message.text === 'string' ? message.text : '';
      if (text === (session.pendingPartial ?? this.partialText())) return;
      clearTimeout(session.settleTimer);
      session.pendingPartial = text;
      // Keep the first words immediate; coalesce subsequent preview updates.
      if (!this.partialText()) this.publishPartial(session);
      else if (!session.partialTimer)
        session.partialTimer = setTimeout(
          () => this.publishPartial(session),
          80
        );
    } else if (
      type === 'committed_transcript' ||
      type === 'committed_transcript_with_timestamps'
    ) {
      const text = typeof message.text === 'string' ? message.text.trim() : '';
      // Empty commits can arrive during silence. Never erase captured words
      // or mark them final merely because an empty event arrived.
      if (!text) {
        if (
          session.commitSent &&
          this.status() === 'stopping' &&
          !this.partialText() &&
          !session.pendingPartial
        ) {
          this.completeStop(session, true);
        }
        return;
      }
      const paired =
        type === 'committed_transcript_with_timestamps'
          ? session.pendingTimed.indexOf(text)
          : -1;
      if (paired >= 0) {
        session.pendingTimed.splice(paired, 1);
        return;
      }
      clearTimeout(session.partialTimer);
      session.partialTimer = undefined;
      session.pendingPartial = undefined;
      this.zone.run(() => {
        if (text)
          this.committedSegments.update((segments) => [
            ...segments,
            { text, timestamp: Date.now() },
          ]);
        this.partialText.set('');
      });
      if (type === 'committed_transcript' && text) {
        session.pendingTimed.push(text);
        session.pendingTimed = session.pendingTimed.slice(-8);
      }
      if (session.commitSent && this.status() === 'stopping') {
        clearTimeout(session.settleTimer);
        session.settleTimer = setTimeout(
          () => this.completeStop(session, true),
          120
        );
      }
    } else if (
      typeof type === 'string' &&
      (type.includes('error') ||
        [
          'invalid_request',
          'quota_exceeded',
          'rate_limited',
          'queue_overflow',
          'resource_exhausted',
          'session_time_limit_exceeded',
          'chunk_size_exceeded',
          'insufficient_audio_activity',
          'commit_throttled',
          'unaccepted_terms',
        ].includes(type))
    ) {
      this.fail(
        session,
        'Voice input stopped. Your captured text has been kept. Please try again.'
      );
    }
  }

  private publishPartial(session: VoiceSession): void {
    clearTimeout(session.partialTimer);
    session.partialTimer = undefined;
    if (!this.current(session) || session.pendingPartial === undefined) return;
    const text = session.pendingPartial;
    session.pendingPartial = undefined;
    this.zone.run(() => this.partialText.set(text));
  }

  private preservePartial(session: VoiceSession): void {
    this.publishPartial(session);
    const partial = this.partialText().trim();
    if (partial)
      this.committedSegments.update((segments) => [
        ...segments,
        { text: partial, timestamp: Date.now() },
      ]);
    this.partialText.set('');
  }

  private completeStop(session: VoiceSession, confirmed: boolean): void {
    if (!this.current(session)) return;
    this.preservePartial(session);
    const text = this.fullTranscript(),
      resolve = session.resolveStop;
    this.dispose(session);
    this.session = undefined;
    this.zone.run(() => {
      this.finalizationIncomplete.set(!confirmed);
      this.status.set('idle');
    });
    resolve?.(text);
  }

  private fail(session: VoiceSession, message: string): void {
    if (!this.current(session)) return;
    this.preservePartial(session);
    const text = this.fullTranscript(),
      resolve = session.resolveStop;
    this.dispose(session);
    this.session = undefined;
    this.zone.run(() => {
      this.errorMessage.set(message);
      this.finalizationIncomplete.set(!!text);
      this.status.set('error');
    });
    resolve?.(text);
  }

  private recordingLimit(session: VoiceSession): void {
    if (!this.current(session) || this.status() !== 'recording') return;
    this.zone.run(() => this.stoppedByTimeout.set(true));
    void this.stopTranscription(session.owner);
  }

  private async prepareAudio(session: VoiceSession): Promise<void> {
    const context = (session.context = new AudioContext({
      sampleRate: this.SAMPLE_RATE,
    }));
    if (context.sampleRate !== this.SAMPLE_RATE)
      throw new Error('Unsupported audio rate');
    const resume =
      context.state === 'suspended' ? context.resume() : Promise.resolve();
    const url = URL.createObjectURL(
      new Blob([PCM_WORKLET_SOURCE], { type: 'application/javascript' })
    );
    const module = Promise.resolve()
      .then(() => context.audioWorklet.addModule(url))
      .finally(() => URL.revokeObjectURL(url));
    const media = navigator.mediaDevices
      .getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })
      .then((stream) => {
        if (!this.current(session)) {
          stream.getTracks().forEach((track) => track.stop());
          throw new Error('Voice input cancelled');
        }
        session.stream = stream;
      });
    await Promise.all([resume, module, media]);
    if (!this.current(session)) return;
    this.zone.runOutsideAngular(() => {
      session.source = context.createMediaStreamSource(session.stream!);
      session.worklet = new AudioWorkletNode(context, 'nova-pcm-processor');
      session.worklet.port.onmessage = (event) => {
        if (!this.current(session)) return;
        const data = event.data;
        if (data.type === 'flushed') session.onFlushed?.();
        else if (data.type === 'limit') this.recordingLimit(session);
        else if (data.type === 'overflow')
          this.fail(
            session,
            'Voice input stopped because audio processing fell behind. Your captured text has been kept.'
          );
        else if (data.type === 'audio' && data.buffer instanceof ArrayBuffer) {
          try {
            if (session.ws?.readyState !== WebSocket.OPEN)
              throw new Error('Connection closed');
            if (session.ws.bufferedAmount > this.MAX_SOCKET_BUFFERED_BYTES) {
              this.fail(
                session,
                'Voice input stopped because the connection fell behind. Your captured text has been kept.'
              );
              return;
            }
            session.ws.send(
              JSON.stringify({
                message_type: 'input_audio_chunk',
                audio_base_64: pcmToBase64(data.buffer),
                commit: false,
              })
            );
            session.audioSent = true;
            session.worklet?.port.postMessage(
              { type: 'recycle', buffer: data.buffer },
              [data.buffer]
            );
          } catch {
            this.fail(
              session,
              'Voice input disconnected. Your captured text has been kept.'
            );
          }
        }
      };
    });
  }

  private dispose(session: VoiceSession): void {
    session.disposed = true;
    for (const timer of [
      session.recordingTimer,
      session.handshakeTimer,
      session.partialTimer,
      session.finalTimer,
      session.settleTimer,
    ])
      clearTimeout(timer);
    session.rejectConnection?.(new Error('Voice input cancelled'));
    session.rejectConnection = undefined;
    if (session.ws) {
      session.ws.onmessage = null;
      session.ws.onerror = null;
      session.ws.onclose = null;
      if (
        session.ws.readyState === WebSocket.OPEN ||
        session.ws.readyState === WebSocket.CONNECTING
      )
        session.ws.close();
    }
    if (session.worklet) {
      session.worklet.port.onmessage = null;
      session.worklet.disconnect();
      session.worklet.port.close();
    }
    session.source?.disconnect();
    session.stream?.getTracks().forEach((track) => track.stop());
    void session.context?.close().catch(() => {});
  }
}
